import { z } from 'zod';

/**
 * File-access module (G19, ADR-0032): tenant and staff file access to
 * resource volumes via SFTPGo virtual users. The panel is the source of
 * truth — these shapes describe panel entities; the SFTPGo wire format lives
 * in the API's sftpgo client, never here.
 */

// ── Value vocabulary ────────────────────────────────────────────────

/** Descriptor keys this module registers (ADR-0011 requires the `files_` prefix). */
export const FILES_FEATURE_KEY = 'files_file_access';
export const FILES_SFTP_USERS_LIMIT = 'files_sftp_users';
/** Short-lived, audited commands inside an owned resource container. */
export const FILES_CONSOLE_FEATURE_KEY = 'files_resource_console';

/**
 * State of a subscription's file access:
 *  - active:    the SFTPGo user can log in;
 *  - suspended: disabled by a subscription suspension (auto-restored on
 *               reactivation);
 *  - disabled:  disabled by staff (a reactivation must NOT resurrect it).
 */
export const FILE_ACCESS_STATUSES = ['active', 'suspended', 'disabled', 'expired'] as const;
export type FileAccessStatus = (typeof FILE_ACCESS_STATUSES)[number];

/** Per-folder access mode. */
export const FILE_FOLDER_MODES = ['rw', 'ro'] as const;
export type FileFolderMode = (typeof FILE_FOLDER_MODES)[number];

export const FILE_ACCOUNT_TYPES = ['tenant', 'service'] as const;
export type FileAccountType = (typeof FILE_ACCOUNT_TYPES)[number];

/** A durable, tenant-owned selection. Host paths are resolved server-side. */
export interface FileFolderGrant {
  resourceId: string;
  volumeName: string;
  /** `.` is the volume root; otherwise a normalized relative directory. */
  relativePath: string;
  mode: FileFolderMode;
}

/** Tenant-safe volume choice shown while a grant is created or edited. */
export interface FileVolumeOptionDto {
  resourceId: string;
  resourceName: string;
  volumeName: string;
}

// ── Provisioned folder (a resource volume exposed to the tenant) ─────

export interface SftpFolderDto {
  /** Ledger resource that owns the volume. */
  resourceId: string;
  resourceName: string;
  /** Coolify's Docker volume name (UUID-suffixed). */
  volumeName: string;
  /** Selected directory inside the volume (`.` means the volume root). */
  relativePath: string;
  /** Host path of the volume on the server (staff-only; stripped for tenants). */
  hostPath: string;
  /** Where the folder appears in the tenant's SFTP/WebClient tree. */
  virtualPath: string;
  mode: FileFolderMode;
}

/** Tenant-safe folder view: what path, read-only or not — never the host path. */
export interface PortalSftpFolderDto {
  virtualPath: string;
  relativePath: string;
  mode: FileFolderMode;
}

// ── The SFTP user (staff view) ──────────────────────────────────────

export interface SftpUserDto {
  id: string;
  subscriptionId: string;
  label: string;
  accountType: FileAccountType;
  /** Which Coolify server / SFTPGo instance serves this user. */
  serverUuid: string;
  username: string;
  homeDir: string;
  /** Quota ceiling in MB (from the plan's disk_mb); null = unlimited. */
  quotaMb: number | null;
  /** Provisioned read-only across every folder. */
  readOnly: boolean;
  status: FileAccessStatus;
  expiresAt: string | null;
  allowedIps: string[];
  deniedIps: string[];
  maxSessions: number;
  folders: SftpFolderDto[];
  /** Connection details resolved from the serving instance (null if unregistered). */
  sftpHost: string | null;
  sftpPort: number | null;
  webClientUrl: string | null;
  /** Last reconcile/provisioning error surfaced to staff (null when healthy). */
  lastError: string | null;
  lastReconcileAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Freshly issued SFTP credentials — returned exactly once, on provisioning or
 * a password reset (the reveal-once idiom, ADR-0007). The password is never
 * stored and never returned by any read endpoint.
 */
export interface SftpCredentialDto {
  accountId: string;
  username: string;
  password: string;
  sftpHost: string | null;
  sftpPort: number | null;
  webClientUrl: string | null;
  expiresAt: string | null;
}

/** The file-access panel on a subscription detail page (staff). */
export interface SubscriptionFileAccessDto {
  subscriptionId: string;
  /** The files module is enabled on this installation. */
  moduleEnabled: boolean;
  /** At least one SFTPGo instance is registered. */
  configured: boolean;
  /** The subscription's plan grants the file_access feature. */
  featureEnabled: boolean;
  /** Effective files_sftp_users limit (null = unlimited). */
  limit: number | null;
  /** SFTP users currently provisioned for the subscription. */
  used: number;
  /** Compatibility alias for the first account. New UIs use `users`. */
  user: SftpUserDto | null;
  users: SftpUserDto[];
  availableFolders: FileVolumeOptionDto[];
  consoleEnabled: boolean;
  consoleResources: Array<{ id: string; name: string; kind: string }>;
}

// ── Provisioning / mutation inputs ──────────────────────────────────

const relativeFolderSchema = z
  .string()
  .trim()
  .min(1)
  .max(500)
  .transform(
    (value) => value.replace(/\\/g, '/').replace(/\/+/g, '/').replace(/\/$/, '') || '.',
  )
  .refine(
    (value) =>
      value === '.' ||
      (!value.startsWith('/') && !value.split('/').some((part) => part === '..')),
    {
      message: 'Folder must be a relative path without parent traversal',
    },
  );

const cidrSchema = z
  .string()
  .trim()
  .min(3)
  .max(80)
  .refine((value) => {
    const [address, prefix, extra] = value.split('/');
    if (
      !address ||
      prefix === undefined ||
      extra !== undefined ||
      !/^\d+$/.test(prefix)
    ) {
      return false;
    }
    const bits = Number(prefix);
    if (address.includes(':')) {
      return bits >= 0 && bits <= 128 && /^[0-9a-f:.]+$/i.test(address);
    }
    const octets = address.split('.');
    return (
      bits >= 0 &&
      bits <= 32 &&
      octets.length === 4 &&
      octets.every((part) => /^\d{1,3}$/.test(part) && Number(part) <= 255)
    );
  }, 'Use an IPv4 or IPv6 network in CIDR notation');

export const fileFolderGrantSchema = z.object({
  resourceId: z.string().uuid(),
  volumeName: z.string().trim().min(1).max(255),
  relativePath: relativeFolderSchema.default('.'),
  mode: z.enum(FILE_FOLDER_MODES).default('rw'),
});

export const provisionFileAccessSchema = z.object({
  label: z.string().trim().min(1).max(100).default('File access'),
  accountType: z.enum(FILE_ACCOUNT_TYPES).default('tenant'),
  /** Legacy whole-subscription toggle; explicit folder modes take precedence. */
  readOnly: z.boolean().optional().default(false),
  /** Omitted only for legacy first-account callers; additional users must select folders. */
  folders: z.array(fileFolderGrantSchema).min(1).max(100).optional(),
  quotaMb: z.number().int().min(1).max(2_147_483_647).nullable().optional(),
  expiresAt: z.string().datetime({ offset: true }).nullable().optional(),
  allowedIps: z.array(cidrSchema).max(50).default([]),
  deniedIps: z.array(cidrSchema).max(50).default([]),
  maxSessions: z.number().int().min(1).max(20).default(2),
});
export type ProvisionFileAccessInput = z.infer<typeof provisionFileAccessSchema>;

export const updateFileAccessSchema = z
  .object({
    label: z.string().trim().min(1).max(100).optional(),
    accountType: z.enum(FILE_ACCOUNT_TYPES).optional(),
    readOnly: z.boolean().optional(),
    folders: z.array(fileFolderGrantSchema).min(1).max(100).optional(),
    quotaMb: z.number().int().min(1).max(2_147_483_647).nullable().optional(),
    expiresAt: z.string().datetime({ offset: true }).nullable().optional(),
    allowedIps: z.array(cidrSchema).max(50).optional(),
    deniedIps: z.array(cidrSchema).max(50).optional(),
    maxSessions: z.number().int().min(1).max(20).optional(),
  })
  .refine((v) => Object.values(v).some((value) => value !== undefined), {
    message: 'Nothing to update',
  });
export type UpdateFileAccessInput = z.infer<typeof updateFileAccessSchema>;

// ── Instance registry (admin settings) ──────────────────────────────

/**
 * One SFTPGo instance, keyed by the Coolify server it serves (ADR-0032). The
 * admin password is a write-only secret stored out-of-band, never in this DTO.
 */
export const filesInstanceSchema = z.object({
  /** Coolify server UUID this instance runs on and serves volumes for. */
  serverUuid: z.string().trim().min(1).max(200),
  /** Admin REST API base URL (e.g. https://files.example.com). */
  adminBaseUrl: z
    .string()
    .trim()
    .url()
    .transform((u) => u.replace(/\/+$/, '')),
  adminUsername: z.string().trim().min(1).max(200),
  /** Admin password. Omit to keep the stored one. */
  adminPassword: z.string().min(1).optional(),
  /** Public SFTP host tenants connect to. */
  sftpHost: z.string().trim().min(1).max(253),
  sftpPort: z.number().int().min(1).max(65535).default(2022),
  /** End-user WebClient base URL (browser file manager). */
  webClientUrl: z
    .string()
    .trim()
    .url()
    .transform((u) => u.replace(/\/+$/, '')),
});
export type FilesInstanceInput = z.infer<typeof filesInstanceSchema>;

export const filesSettingsSchema = z.object({
  instances: z.array(filesInstanceSchema).max(50),
});
export type FilesSettingsInput = z.infer<typeof filesSettingsSchema>;

export interface FilesInstanceDto {
  serverUuid: string;
  serverName: string | null;
  adminBaseUrl: string;
  adminUsername: string;
  sftpHost: string;
  sftpPort: number;
  webClientUrl: string;
  /** An admin password secret is stored for this instance. */
  hasSecret: boolean;
}

export interface FilesSettingsDto {
  instances: FilesInstanceDto[];
}

/** Files module status page (staff): instances + last reconcile summary. */
export interface FilesStatusDto {
  moduleEnabled: boolean;
  configured: boolean;
  instances: FilesInstanceDto[];
  lastReconcile: FilesReconcileSummaryDto | null;
}

export interface FilesReconcileSummaryDto {
  ranAt: string;
  durationMs: number;
  usersChecked: number;
  usersRepaired: number;
  errors: number;
}

// ── Portal (tenant) ─────────────────────────────────────────────────

/** Tenant-facing file-access view: connection info, never host paths. */
export interface PortalFileAccessDto {
  moduleEnabled: boolean;
  featureEnabled: boolean;
  access: PortalFileAccountDto | null;
  accounts: PortalFileAccountDto[];
}

export interface PortalFileAccountDto {
  id: string;
  label: string;
  username: string;
  status: FileAccessStatus;
  sftpHost: string | null;
  sftpPort: number | null;
  webClientUrl: string | null;
  quotaMb: number | null;
  expiresAt: string | null;
  folders: PortalSftpFolderDto[];
}

// ── Resource console ───────────────────────────────────────────────

export const RESOURCE_CONSOLE_OPERATIONS = [
  'working-directory',
  'list-files',
  'disk-usage',
  'php-version',
  'node-version',
  'python-version',
] as const;
export type ResourceConsoleOperation = (typeof RESOURCE_CONSOLE_OPERATIONS)[number];

export const RESOURCE_CONSOLE_SESSION_STATUSES = [
  'active',
  'stopped',
  'revoked',
  'expired',
] as const;
export type ResourceConsoleSessionStatus =
  (typeof RESOURCE_CONSOLE_SESSION_STATUSES)[number];

const consolePathSchema = z
  .string()
  .trim()
  .min(1)
  .max(240)
  .default('.')
  .refine(
    (value) =>
      !value.startsWith('/') &&
      !value.includes('\\') &&
      !value.split('/').some((part) => part === '..') &&
      !/(^|\/)(?:\.env|proc|sys|dev|run|var\/run)(?:\/|$)/i.test(value) &&
      !/(?:secret|credential|token|private[_-]?key)/i.test(value),
    'Path is outside the console-safe workspace',
  );

export const startResourceConsoleSchema = z.object({
  durationMinutes: z.number().int().min(5).max(30).default(15),
});
export type StartResourceConsoleInput = z.infer<typeof startResourceConsoleSchema>;

export const runResourceConsoleCommandSchema = z.object({
  operation: z.enum(RESOURCE_CONSOLE_OPERATIONS),
  path: consolePathSchema.optional(),
});
export type RunResourceConsoleCommandInput = z.infer<
  typeof runResourceConsoleCommandSchema
>;

export interface ResourceConsoleSessionDto {
  id: string;
  resourceId: string;
  resourceName: string;
  status: ResourceConsoleSessionStatus;
  expiresAt: string;
  commandCount: number;
  maxCommands: number;
  startedAt: string;
  stoppedAt: string | null;
  allowedOperations: ResourceConsoleOperation[];
}

export interface ResourceConsoleCommandResultDto {
  operation: ResourceConsoleOperation;
  output: string;
  exitCode: number;
  durationMs: number;
  truncated: boolean;
  redactions: number;
}
